import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle, ArrowUpRight, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SERVICE_OPTIONS = [
  "Web Development",
  "Social Media Management",
  "Live Streaming",
  "Graphic Designing",
  "Other",
];

const WHATSAPP_NUMBER = "918097208707";
const PHONE_DISPLAY = "+91 80972 08707";
const EMAIL = "mahaview1@gmail.com";
const ADDRESS = "Mumbai, India";
const HOURS = "Mon – Sat · 9 AM – 7 PM";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      const lines = [
        `Hi Mahaview Enterprises, I'd like to start a project.`,
        ``,
        `Name: ${form.name.trim()}`,
        `Email: ${form.email.trim()}`,
        form.phone.trim() ? `Phone: ${form.phone.trim()}` : null,
        form.service ? `Service: ${form.service}` : null,
        ``,
        `Message:`,
        form.message.trim(),
      ].filter(Boolean);
      const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
      window.open(waUrl, "_blank", "noopener,noreferrer");
      toast.success("Opening WhatsApp to send your message…");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Couldn't open WhatsApp. Please call or email us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 md:py-32 px-5 sm:px-6 md:px-10 border-t border-white/5 overflow-hidden">
      <div className="max-w-7xl mx-auto grid grid-cols-12 gap-8 md:gap-10">
        <div className="col-span-12 md:col-span-5 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5A00] mb-4 md:mb-5">/ Contact</div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight mb-5 md:mb-6 break-words">
            Let's start<br />
            <span className="text-[#FF5A00]">your project.</span>
          </h2>
          <p className="text-zinc-400 max-w-md mb-10 leading-relaxed">
            Get in touch with us for a free consultation and quote. Quick response guaranteed.
          </p>

          <ul className="space-y-5 mb-8">
            <li className="flex items-start gap-4">
              <Phone size={18} className="text-[#FF5A00] mt-1" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Phone</div>
                <a href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`} className="text-base text-white hover:text-[#FF5A00] transition-colors">
                  {PHONE_DISPLAY}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Mail size={18} className="text-[#FF5A00] mt-1" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Email</div>
                <a href={`mailto:${EMAIL}`} className="text-base text-white hover:text-[#FF5A00] transition-colors">
                  {EMAIL}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <MapPin size={18} className="text-[#FF5A00] mt-1" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Location</div>
                <div className="text-base text-white">{ADDRESS}</div>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Clock size={18} className="text-[#FF5A00] mt-1" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Working Hours</div>
                <div className="text-base text-white">{HOURS}</div>
              </div>
            </li>
          </ul>

          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noreferrer"
            data-testid="whatsapp-cta"
            className="inline-flex items-center gap-3 border border-white/20 hover:border-[#FF5A00] px-5 py-3 text-sm transition-colors group"
          >
            <MessageCircle size={16} className="text-[#FF5A00]" />
            <span>Chat on WhatsApp · Quick Response</span>
            <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="col-span-12 md:col-span-7 mv-card p-5 sm:p-6 md:p-10 flex flex-col gap-5 min-w-0"
        >
          <h3 className="font-display text-2xl font-semibold mb-1">Send us a message</h3>
          <p className="text-sm text-zinc-400 mb-2">We respond within a few business hours.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Full Name" required>
              <input
                data-testid="contact-input-name"
                type="text"
                value={form.name}
                onChange={update("name")}
                placeholder="Your full name"
                className="mv-input"
                required
              />
            </Field>
            <Field label="Email" required>
              <input
                data-testid="contact-input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@company.com"
                className="mv-input"
                required
              />
            </Field>
            <Field label="Phone">
              <input
                data-testid="contact-input-phone"
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+91 ..."
                className="mv-input"
              />
            </Field>
            <Field label="Service">
              <Select value={form.service} onValueChange={update("service")}>
                <SelectTrigger
                  data-testid="contact-select-service"
                  className="bg-transparent border-white/20 hover:border-white/40 text-white h-[46px] rounded-sm"
                >
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="bg-[#0d0d0d] border-white/10 text-white">
                  {SERVICE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`service-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Message" required>
            <textarea
              data-testid="contact-input-message"
              rows={5}
              value={form.message}
              onChange={update("message")}
              placeholder="Tell us about your project goal, timeline & target audience..."
              className="mv-input resize-none"
              required
            />
          </Field>

          <button
            type="submit"
            data-testid="contact-submit-button"
            disabled={loading}
            className="mv-btn-primary inline-flex items-center justify-center gap-2 px-7 py-4 text-sm font-medium rounded-sm disabled:opacity-60 disabled:cursor-not-allowed mt-2 self-start"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Sending...
              </>
            ) : (
              <>
                Send Message
                <ArrowUpRight size={16} />
              </>
            )}
          </button>

          <style>{`
            .mv-input {
              width: 100%;
              background: transparent;
              color: #fff;
              padding: 12px 14px;
              border: 1px solid rgba(255,255,255,0.18);
              border-radius: 2px;
              font-family: inherit;
              font-size: 14px;
              outline: none;
              transition: border-color 0.2s ease, background 0.2s ease;
            }
            .mv-input::placeholder { color: rgba(255,255,255,0.35); }
            .mv-input:focus { border-color: #FF5A00; background: rgba(255,90,0,0.04); }
          `}</style>
        </form>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">
        {label}
        {required && <span className="text-[#FF5A00] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
